import { useMemo } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { uniquePlainIncomeFailReasons } from '../../lib/applicationJourneyCopy';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';

function formatMoney(n: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatWhen(iso: unknown): string | null {
  if (typeof iso !== 'string' || !iso.trim()) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatFrequency(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) return '—';
  const u = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
  const map: Record<string, string> = {
    WEEKLY: 'Weekly',
    BIWEEKLY: 'Biweekly',
    SEMI_MONTHLY: 'Semi-monthly',
    SEMIMONTHLY: 'Semi-monthly',
    MONTHLY: 'Monthly',
    IRREGULAR: 'Irregular',
  };
  return map[u] ?? raw.replace(/_/g, ' ');
}

function formatGateOutcome(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) return '—';
  const u = raw.trim().toUpperCase();
  if (u === 'PASS' || u === 'OK' || u === 'SUCCESS') return 'Passed';
  if (u === 'FAIL' || u === 'FAILED') return 'Failed';
  if (u === 'REVIEW' || u === 'WARN') return 'Needs review';
  if (u === 'NOT_RUN' || u === 'PENDING') return 'Not run';
  return raw.replace(/_/g, ' ');
}

function formatVerdict(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) return '—';
  const u = raw.trim().toUpperCase();
  if (/PASS|SUCCESS|VERIFIED|OK|COMPLETE/.test(u)) return 'Verified';
  if (/FAIL|REJECT|DECLIN|INSUFFICIENT/.test(u)) return 'Failed';
  if (/REVIEW|MANUAL|WARN|PENDING/.test(u)) return 'Needs review';
  return raw.replace(/_/g, ' ');
}

function statusPresentation(statusRaw: string): {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
} {
  const u = statusRaw.toUpperCase();
  if (/PASS|SUCCESS|VERIFIED|OK|COMPLETE/.test(u)) return { label: 'Passed', color: 'success' };
  if (/FAIL|REJECT|DECLIN|INSUFFICIENT/.test(u)) return { label: 'Did not pass', color: 'error' };
  if (/REVIEW|MANUAL|WARN|PENDING/.test(u)) return { label: 'Needs review', color: 'warning' };
  return { label: statusRaw.replace(/_/g, ' ') || 'Recorded', color: 'default' };
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function asNum(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function asStr(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0, py: 0.85, px: 1.25 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 600, fontSize: '0.75rem', mb: 0.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, fontSize: '0.875rem', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function ApplicationIncomeResultPanel({
  row,
  detail = null,
  isLatest = true,
}: {
  row: Record<string, unknown>;
  detail?: unknown | null;
  isLatest?: boolean;
}) {
  const statusRaw = String(row.finalStatus ?? row.status ?? '');
  const status = statusPresentation(statusRaw);
  const metrics = asRecord(row.metrics) ?? {};
  const gates = asRecord(row.gates) ?? {};
  const detailRec = asRecord(detail);
  const detailMetrics = asRecord(detailRec?.metrics) ?? {};
  const detailGates = asRecord(detailRec?.gates) ?? {};
  const detailOutcomes = asRecord(detailRec?.outcomes) ?? {};

  const failReasons = useMemo(
    () =>
      uniquePlainIncomeFailReasons(
        (Array.isArray(detailRec?.failReasons) ? detailRec.failReasons : null) ??
          (Array.isArray(row.failReasons) ? row.failReasons : null) ??
          (Array.isArray(metrics.failReasons) ? metrics.failReasons : []) ??
          [],
      ),
    [detailRec?.failReasons, row.failReasons, metrics.failReasons],
  );

  const income =
    asNum(detailMetrics.normalizedMonthlyIncome) ?? asNum(metrics.normalizedMonthlyIncome);
  const primaryPayor = asStr(detailMetrics.primaryPayor) ?? asStr(metrics.primaryPayor);
  const frequency =
    asStr(detailMetrics.frequencyClassification) ?? asStr(metrics.frequencyClassification);
  const verdictRaw =
    asStr(detailRec?.verdict) ??
    asStr(detailRec?.finalStatus) ??
    asStr(row.finalStatus) ??
    asStr(row.verdict);
  const recurrence =
    asStr(detailGates.RECURRENCE) ??
    asStr(detailOutcomes.recurrenceOutcome) ??
    asStr(gates.RECURRENCE);
  const stability =
    asStr(detailGates.STABILITY) ??
    asStr(detailOutcomes.stabilityOutcome) ??
    asStr(gates.STABILITY);
  const arbitration =
    asStr(detailGates.ARBITRATION) ??
    asStr(detailOutcomes.arbitrationOutcome) ??
    asStr(gates.ARBITRATION);
  const depositsUsed = asNum(detailMetrics.depositsUsed) ?? asNum(metrics.depositsUsed);
  const stabilityScore = asNum(detailMetrics.stabilityScore) ?? asNum(metrics.stabilityScore);
  const confidence = asNum(detailRec?.confidenceScore) ?? asNum(row.confidenceScore);
  const verifiedLabel = formatWhen(detailRec?.verifiedAt ?? row.verifiedAt);

  const rows: Array<{ label: string; value: string; hide?: boolean }> = [
    { label: 'Monthly income', value: income != null ? formatMoney(income) : '—' },
    { label: 'Primary payor', value: primaryPayor || '—' },
    { label: 'Frequency detected', value: formatFrequency(frequency) },
    { label: 'Verdict', value: formatVerdict(verdictRaw) },
    { label: 'Recurrence', value: formatGateOutcome(recurrence) },
    { label: 'Stability', value: formatGateOutcome(stability) },
    { label: 'Arbitration', value: formatGateOutcome(arbitration) },
    {
      label: 'Deposits used',
      value: depositsUsed != null ? String(depositsUsed) : '—',
      hide: depositsUsed == null,
    },
    {
      label: 'Stability score',
      value: stabilityScore != null ? `${Math.round(stabilityScore)}` : '—',
      hide: stabilityScore == null,
    },
    {
      label: 'Confidence',
      value: confidence != null ? `${Math.round(confidence)}` : '—',
      hide: confidence == null,
    },
    { label: 'Checked', value: verifiedLabel ?? '—' },
  ];
  const visible = rows.filter((r) => !r.hide);

  return (
    <ApplicationSectionCard
      title="Income result"
      subtitle={
        isLatest
          ? 'Monthly amount, pay frequency, verdict, and gate outcomes from this verification.'
          : 'Earlier bank income result (not the current outcome).'
      }
      icon={<FactCheckRoundedIcon fontSize="small" />}
      endAdornment={
        <Chip
          size="small"
          color={status.color}
          variant="outlined"
          label={status.label}
          sx={{ fontWeight: 700, height: 22 }}
        />
      }
      paperSx={
        isLatest
          ? {
              borderColor: (t) => alpha(t.palette.primary.main, 0.28),
            }
          : undefined
      }
      data-testid="income-result-panel"
    >
      <Box
        sx={{
          px: { xs: 0.5, sm: 0.75 },
          py: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: (t) => (t.palette.mode === 'light' ? '#F7F8FA' : alpha(t.palette.common.white, 0.04)),
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          columnGap: { sm: 1 },
          rowGap: 0.25,
        }}
      >
        {visible.map((item) => (
          <Kv key={item.label} label={item.label} value={item.value} />
        ))}
      </Box>

      {failReasons.length > 0 ? (
        <Stack spacing={0.35} sx={{ mt: 1.25 }}>
          {failReasons.slice(0, 3).map((line) => (
            <Typography key={line} variant="body2" color="text.secondary">
              {line}
            </Typography>
          ))}
        </Stack>
      ) : null}
    </ApplicationSectionCard>
  );
}

